/**
 * Where a tested embryo is now, read from its cryo vault record. The embryo
 * report links each embryo to that record through `embryoSpecimenId`.
 */
import { isInFrozenStorage } from "../frozenSpecimenAvailability";
import type { Specimen } from "../useSpecimens";

export type EmbryoStorageLocation = {
  tank: string | null;
  canister: string | null;
  cane: string | null;
  straw: string | null;
};

export type EmbryoStorageState =
  | {
      kind: "frozen";
      facility: string | null;
      frozenOn: string | null;
      location: EmbryoStorageLocation;
    }
  | { kind: "transferred"; date: string | null; cycleId: string | null }
  | { kind: "thawed"; date: string | null }
  | { kind: "discarded" }
  | { kind: "donated" }
  | { kind: "in_transit" };

type StorageSpecimen = Pick<
  Specimen,
  "status" | "storageLocations" | "specimenUsages"
>;

function findUsage(
  specimen: StorageSpecimen,
  usage: "transfer" | "thaw" | "freeze",
) {
  return specimen.specimenUsages.find((candidate) => candidate.usage === usage);
}

function usageDate(
  specimen: StorageSpecimen,
  usage: "transfer" | "thaw" | "freeze",
): string | null | undefined {
  const event = findUsage(specimen, usage);
  return event ? (event.date ?? null) : undefined;
}

export function getEmbryoStorageState(
  specimen: StorageSpecimen,
): EmbryoStorageState | null {
  const transfer = findUsage(specimen, "transfer");
  if (transfer) {
    return {
      kind: "transferred",
      date: transfer.date ?? null,
      cycleId: transfer.cycleId ?? null,
    };
  }

  if (isInFrozenStorage(specimen)) {
    const storage = specimen.storageLocations.find(
      (location) => location.isActive && location.storageType === "frozen",
    );
    return {
      kind: "frozen",
      facility: storage?.facility ?? null,
      frozenOn: storage?.storedFrom ?? usageDate(specimen, "freeze") ?? null,
      location: {
        tank: storage?.tankNumber ?? null,
        canister: storage?.canisterNumber ?? null,
        cane: storage?.caneNumber ?? null,
        straw: storage?.strawNumber ?? null,
      },
    };
  }

  const thawedOn = usageDate(specimen, "thaw");
  if (thawedOn !== undefined) return { kind: "thawed", date: thawedOn };

  switch (specimen.status) {
    case "discarded":
    case "donated":
    case "in_transit":
      return { kind: specimen.status };
    default:
      return null;
  }
}
